import {ChangeDetectorRef, Component, EventEmitter, inject, OnDestroy, OnInit, Output} from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {dispatchDeletedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  AdministrationResultWithAvailableModuleActionsOld,
  BokkzAlgDataService,
  BokkzAlgLogic,
  BokkzClient,
  BokkzMumaClient,
  BokkzTslDataService,
  ClientAddons,
  ClientEntity,
  CustomerClient,
  CustomerQueryClient,
  CustomerRequest,
  CustomerResponse,
  FiscalIdentifierType,
  IntermediaryEntity,
  MenuInterface,
  MumaComponent,
  MumaEntity,
  OpenRelationDetailsEvent,
  ProblemDetails,
  RelationColumn,
  RelationSearchCriteria,
  RelationSearchResult,
  RelationSearchResults,
  RelationType,
  relationTypeToDutchTranslationsMap,
  relationTypeToPluralDutchTranslationsMap,
  SalutationType,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ContextMenuItem} from '@snelstart/ui-context-menu';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {ToastService} from '@snelstart/ui-toast';
import {ToggleFilterConfiguration} from '@snelstart/util-integration';
import {DialogService, SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {SharedUiCardModule} from '@snelstart/ui-card';
import {dispatchSnelStartEvent} from '@snelstart/util-events';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {SharedUiBadgeModule} from '@snelstart/ui-badge';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-client-list',
  templateUrl: './feature-tax-client-list.component.html',
  styleUrls: ['./feature-tax-client-list.component.scss'],
  imports: [
    SharedUiTableModule,
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiAlertModule,
    SharedUiIconModule,
    SharedUiCardModule,
    SharedUiTooltipModule,
    SharedUiBadgeModule,
    MumaComponent,
  ],
})
export class FeatureTaxClientListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<string>();

  public readonly dataService = inject(BokkzTslDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly bokkzMumaClient = inject(BokkzMumaClient);
  public readonly algDataService = inject(BokkzAlgDataService);
  private readonly toastService = inject(ToastService);
  private readonly algLogic = inject(BokkzAlgLogic);
  private readonly customerClient = inject(CustomerClient);
  private readonly customerQueryClient = inject(CustomerQueryClient);
  public readonly fb = inject(FormBuilder);
  public readonly cdr = inject(ChangeDetectorRef);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected tableConfig!: TableConfiguration<SearchCriteria, Record<Uncapitalize<keyof typeof RelationColumn>, unknown>>;
  protected COLUMN: { [key in keyof typeof RelationColumn]: Uncapitalize<key> } = Object.fromEntries(
    Object.keys(RelationColumn).map((key) => [key, `${key[0].toLowerCase()}${key.slice(1)}`]),
  ) as { [key in keyof typeof RelationColumn]: Uncapitalize<key> };
  protected displayArchivedRelations = false;
  protected readonly relationTypeToPluralDutchTranslationsMap = relationTypeToPluralDutchTranslationsMap;
  protected readonly relationTypeToDutchTranslationsMap = relationTypeToDutchTranslationsMap;
  protected type = RelationType.Customer as RelationType;
  protected id: any;

  private destroy$ = new Subject<void>();
  private dialogService: DialogService = inject(DialogService);
  private isZeroState = false;
  private tableCriteria!: RelationSearchCriteria;
  public dialogMumaVisible = false;
  public sstRelationId: any;
  public mumaList = [] as MumaEntity[];
  public dialogAdministrationVisible = false;
  public isLoading = false;
  public migratingClients = false;
  public adminLijst = [] as AdministrationResultWithAvailableModuleActionsOld[];
  public adminInputFg: any;
  public adminId = '';
  public showTable = false;
  public clientId: any;
  public clientAddonList = [] as ClientAddons[];
  public options = [] as MenuInterface[];
  public client = {} as ClientEntity;
  public clientName = '';
  public intermediaryEntity = {} as IntermediaryEntity;
  public optionsAministratie: SelectFormControlItem<string>[] = [];
  public showDuplicateDialog = false;
  public duplicateTaxId = '';
  public duplicateCount: number | undefined = 0;

  public optionsJaNee: SelectFormControlItem<boolean | null>[] = [
    {value: null, label: '- Selecteer -'},
    {value: true, label: 'Ja, wel kantooradministratie'},
    {value: false, label: 'Nee, geen kantooradministratie'},
  ];

  async ngOnInit(): Promise<void> {
    this.createFormGroups();
    try {
      // 2. Wait for route parameters
      const params = await firstValueFrom(this.activatedRoute.params);
      this.adminId = params['administrationIdentifier'] ?? 'bokkz';

      // 3. Wait for intermediary data
      const intermediary = await firstValueFrom(
        this.algDataService.intermediary.pipe(
          filter((i): i is IntermediaryEntity => !!i && typeof i.id === 'number' && i.id > 0),
          take(1),
        ),
      );
      if (!intermediary) return;

      // 4. Patch the form
      this.intermediaryEntity = intermediary;

      // 5. Run the main logic after route + intermediary are ready
      await this.handlePrimaryAdministration(intermediary);
    } catch (err) {
      console.error('Error initializing component:', err);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createFormGroups() {
    this.adminInputFg = this.fb.group({
      selectedAdminId: [null, Validators.required],
      kantoorAdministratie: [null as boolean | null, Validators.required],
    });
  }

  private async handlePrimaryAdministration(intermediary: IntermediaryEntity): Promise<void> {
    const sstPrimaryAdminId = intermediary.sstPrimaryAdminId;
    if (!sstPrimaryAdminId || sstPrimaryAdminId.trim().toLowerCase() === '') {
      const admins = await firstValueFrom(this.algLogic.getAllAdministrations());
      if (admins.length > 0 && admins[0].id) {
        this.adminLijst = admins;
        for (const admin of admins) {
          if (admin.type === 1) {
            this.optionsAministratie.push({
              value: String(admin.id),
              label: String(admin.name),
            });
          }
        }
        this.dialogAdministrationVisible = true;
        console.log('No primary admin ID found');
      }
    } else {
      const token = await this.algLogic.verrijkToken(sstPrimaryAdminId);
      if (!token) {
        console.error('Token could not be fetched');
        return;
      }

      //Hier starten we opschonen van relaties
      if (intermediary.hasToken === undefined || !intermediary.hasToken) {
          // this.cleanUp().subscribe();
      }

      this.createTableConfig(
        (searchCriteria: SearchCriteria) =>
          this.algLogic.getPaginated(RelationSearchCriteria.fromSearchCriteria(searchCriteria, true, false)).pipe(
            tap((response) => {
              this.updateCustomerDuplicates(response.results).subscribe((value) => {
                if (value) {
                  this.algDataService.updateRelations(response);
                }
              });
            }),
          ),
        (id: string) => this.algLogic.archiveCustomer(id),
        (id: string) => this.algLogic.deArchiveCustomer(id),
        (id: string) => this.algLogic.deleteCustomer(id),
      );
      this.showTable = true;
    }
  }

  private createTableConfig(
    getRelations: (searchCriteria: SearchCriteria) => Observable<RelationSearchResults>,
    archiveRelation: (id: string) => Observable<CustomerResponse> = () => of(),
    unArchiveRelation: (id: string) => Observable<CustomerResponse> = () => of(),
    deleteRelation: (id: string) => Observable<CustomerResponse> = () => of(),
  ) {
    this.tableConfig = TableConfiguration.create({
      identifier: 'id',
      criteria: new SearchCriteria(),
      groupIdentifier: 'relations',
      options: {
        searchColumnValuesToFilter: (criteria) => {
          const columnName = criteria.columnName as Uncapitalize<keyof typeof RelationColumn>;
          const getDistinctValues = (relationColumn: RelationColumn) =>
            this.algLogic
              .getDistinctValues(relationColumn)
              .pipe(map((response) => new SearchResult(response.distinctValues, response.distinctValues.length)));
          if (columnName === 'isActive') {
            return getDistinctValues(RelationColumn.IsActive);
          }
          if (columnName === 'code') {
            return getDistinctValues(RelationColumn.Code);
          }
          if (columnName === 'fiscalIdentifier') {
            return getDistinctValues(RelationColumn.FiscalIdentifier);
          }
          if (columnName === 'name') {
            return getDistinctValues(RelationColumn.Name);
          }
          if (columnName === 'contactPersonName') {
            return getDistinctValues(RelationColumn.ContactPersonName);
          }
          if (columnName === 'addressPostalCode') {
            return getDistinctValues(RelationColumn.AddressPostalCode);
          }
          if (columnName === 'addressCity') {
            return getDistinctValues(RelationColumn.AddressCity);
          }
          if (columnName === 'phoneNumber') {
            return getDistinctValues(RelationColumn.PhoneNumber);
          }
          if (columnName === 'mobilePhoneNumber') {
            return getDistinctValues(RelationColumn.MobilePhoneNumber);
          }
          if (columnName === 'email') {
            return getDistinctValues(RelationColumn.Email);
          }
          if (columnName === 'valueAddedTaxNumber') {
            return getDistinctValues(RelationColumn.ValueAddedTaxNumber);
          }
          if (columnName === 'numberOfFiscalAuthorizations') {
            return of(new SearchResult([], 0));
          }
          if (columnName === 'defermentArrangementForFilingTaxReturn') {
            return of(new SearchResult(['True', 'False'], 2));
          }
          return of(new SearchResult([], 0));
        },
      },
      search: (criteria) => {
        this.tableCriteria = RelationSearchCriteria.fromSearchCriteria(criteria);
        this.displayArchivedRelations =
          this.tableCriteria.filters?.alphaNumeric
            ?.filter((filter) => filter.columnName === RelationColumn.IsActive)
            .every((filter) => filter.value === 'False') ?? false;

        return getRelations(criteria).pipe(
          map((response) => {

            this.isZeroState =
              response.total === 0 &&
              !this.tableCriteria.searchTerm &&
              !Object.values(criteria.columnFilters || {}).some((filters) => filters.length);

            const items = response.results.map(
              (entity) =>
                ({
                  id: entity.id,
                  code: entity.code,
                  name: entity.name,
                  fiscalIdentifier: entity.fiscalIdentifier,
                  fiscalIdentifierType: entity.fiscalIdentifierType,
                  dateOfBirth: entity.dateOfBirth,
                  mainActivityCode: entity.mainActivityCode,
                  isActive: !this.displayArchivedRelations,
                  contactPersonName: entity.contactPerson ?? '',
                  phoneNumber: entity.phoneNumber ?? '',
                  mobilePhoneNumber: entity.mobilePhoneNumber ?? '',
                  addressPostalCode: entity.postalCode,
                  addressCity: entity.city,
                  email: entity.email,
                  defermentArrangementForFilingTaxReturn: entity.defermentArrangementForFilingTaxReturn,
                  numberOfFiscalAuthorizations: entity.numberOfFiscalAuthorizations,
                }) as Record<Uncapitalize<keyof typeof RelationColumn>, unknown>,
            );
            return new SearchResult(items, response.total);
          }),
        );
      },
      toggleFilterConfig: ToggleFilterConfiguration.create({
        name: RelationColumn.IsActive,
        value: 'True',
        items: [
          {
            label: $localize`Actieve ${relationTypeToPluralDutchTranslationsMap[this.type].toLowerCase()}`,
            value: 'True',
          },
          {
            label: $localize`Gearchiveerde ${relationTypeToPluralDutchTranslationsMap[this.type].toLowerCase()}`,
            value: 'False',
          },
        ],
      }),
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: `Er zijn nog geen ${relationTypeToPluralDutchTranslationsMap[this.type].toLowerCase()}`,
        subText: `Maak je eerste ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} aan`,
        buttonText: `Voeg ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} toe`,
        buttonConfig: ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                // subscriber.next((this.dialogCreateClientVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
        condition: () => of(this.isZeroState),
      }),
      details: (entity) => {
        if (entity.id !== undefined) {
          this.details.next(String(entity.id));
        }
      },
      contextMenu: (entity) => (menu) => {
        const entityId = entity.id as string;
        if (!entityId) return;

        const archiveContextMenuItem: ContextMenuItem<CustomerResponse | undefined> = {
          id: 'archive',
          icon: 'box-archive',
          iconType: 'light',
          name: $localize`Archiveren`,
          onClick: () =>
            archiveRelation(entityId).pipe(
              catchError(() => {
                this.toastService.showErrorMessage(
                  $localize`Het archiveren van ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} ${entity.name} is mislukt`,
                );
                return of();
              }),
            ),
          onResult: () => {
            this.toastService.showSuccessMessage(
              $localize`${relationTypeToDutchTranslationsMap[this.type]} ${entity.name} is succesvol gearchiveerd`,
            );
            dispatchDeletedItemEvent({identifier: entityId});
            this.bokkzClient.getClient(entityId).subscribe((value) => {
              if (value) {
                const clientToPost = {...value, deleted: true};
                this.bokkzClient.postClient(clientToPost).subscribe();
              }
            });
          },
        };

        const deArchiveContextMenuItem: ContextMenuItem<CustomerResponse | undefined> = {
          id: 'dearchive',
          icon: 'inbox-out',
          iconType: 'light',
          name: $localize`Dearchiveren`,
          onClick: () =>
            unArchiveRelation(entityId).pipe(
              catchError(() => {
                this.toastService.showErrorMessage(
                  $localize`Het dearchiveren van ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} ${entity.name as string} is mislukt`,
                );
                return of();
              }),
            ),
          onResult: () => {
            this.toastService.showSuccessMessage(
              $localize`${relationTypeToDutchTranslationsMap[this.type]} ${entity.name as string} is succesvol gedearchiveerd`,
            );
            dispatchDeletedItemEvent({identifier: entityId});
          },
        };

        const deleteContextMenuItem: ContextMenuItem<CustomerResponse> = {
          id: 'delete',
          icon: 'trash',
          iconType: 'light',
          colorType: 'danger',
          name: $localize`Verwijderen`,
          onClick: () =>
            this.dialogService.showConfirmDeleteDialog({
              title: $localize`${relationTypeToDutchTranslationsMap[this.type]} verwijderen`,
              body: $localize`Weet je zeker dat je deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} wilt verwijderen?`,
              onConfirm: () => deleteRelation(entityId),
              onConfirmed: () => {
                this.toastService.showSuccessMessage(
                  $localize`${relationTypeToDutchTranslationsMap[this.type]} '${entity.name}' is succesvol verwijderd`,
                );
                dispatchDeletedItemEvent({identifier: entityId});
              },
              onError: (problemDetails: ProblemDetails) => {
                const errorMessages: string[] = [];
                if (problemDetails?.status === 403) {
                  errorMessages.push(
                    $localize`Je hebt geen rechten om ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} te verwijderen.`,
                  );
                } else {
                  errorMessages.push(
                    $localize`Het verwijderen van de ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()} '${entity.name}' is mislukt:`,
                  );
                  let globalErrors:
                    | {
                    errorCode: string;
                    message: string;
                  }[]
                    | undefined = problemDetails?.['validationErrors']?.global;

                  if ('errors' in problemDetails && Array.isArray(problemDetails['errors'])) {
                    globalErrors = problemDetails['errors'];
                  }
                  if (globalErrors) {
                    const errorCodes: Record<string, string> = {
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      CustomerIsRelatedToAnotherCustomer: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: is ingesteld als corespondentie adres bij een andere klant.`,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      SupplierIsRelatedToPurchaseOrders: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: wordt gebruikt in inkooporders.`,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      SupplierIsRelatedToOutstandingPurchaseOrders: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: wordt gebruikt in open inkooporders.`,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      CustomerIsRelatedToSalesOrder: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: wordt gebruikt in een verkooporder.`,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      CustomerIsRelatedToOutstandingInvoice: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: wordt gebruikt in een openstaande factuur.`,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      CustomerIsRelatedToInvoice: $localize`Deze ${relationTypeToDutchTranslationsMap[this.type].toLowerCase()}:supplierOrCustomer: kan niet worden verwijderd omdat deze wordt gebruikt in een verkooporder of factuur.`,
                    };
                    for (const globalError of globalErrors) {
                      errorMessages.push(errorCodes[globalError.errorCode] ?? globalError.message);
                    }
                  }
                }

                const cancelButton = document.querySelector('#dialogConfirmDeleteCancel');
                if (cancelButton instanceof HTMLElement) {
                  setTimeout(() => {
                    cancelButton.click();
                  }, 0);
                }
                this.toastService.showErrorMessage(errorMessages.join('\n'));
              },
            }),
        };

        if (this.displayArchivedRelations) {
          return (
            menu
              .createGroup()
              .addItem(deArchiveContextMenuItem)
              .addItem(deleteContextMenuItem)
          );
        } else {
          return (
            menu
              .createGroup()
              .addItem(archiveContextMenuItem)
              .addItem({
                id: 'edit',
                icon: 'pencil',
                iconType: 'light',
                name: $localize`Wijzigen`,
                onClick: () => {
                  dispatchSnelStartEvent(OpenRelationDetailsEvent, {
                    id: entityId,
                    type: this.type,
                  });
                },
              } satisfies ContextMenuItem<unknown>)
          );
        }
      },
    });
  }

  async submitAdministrationId(): Promise<void> {
    const sstPrimaryAdminId = this.adminInputFg.get('selectedAdminId')?.value;
    if (!sstPrimaryAdminId) return;

    this.isLoading = true;

    try {
      const token = await this.algLogic.verrijkToken(sstPrimaryAdminId);
      if (!token) return;

      const clients = await firstValueFrom(this.bokkzClient.getClients());

      const clientsToMigrate = clients.filter((c) => c.sstRelationId == null);
      if (clientsToMigrate.length > 0) {
        this.migratingClients = true;
        this.intermediaryEntity.sstPrimaryAdminId = this.adminInputFg.get('selectedAdminId')?.value;
        this.algLogic.postOneIntermediary(this.intermediaryEntity);
      } else {
        this.createTableConfig(
          (searchCriteria: SearchCriteria) =>
            this.algLogic
              .getPaginated(RelationSearchCriteria.fromSearchCriteria(searchCriteria, true, false))
              .pipe(tap((response) => this.algDataService.updateRelations(response))),
          (id: string) => this.algLogic.archiveCustomer(id),
          (id: string) => this.algLogic.deArchiveCustomer(id),
          (id: string) => this.algLogic.deleteCustomer(id),
        );
        this.intermediaryEntity.sstPrimaryAdminId = this.adminInputFg.get('selectedAdminId')?.value;
        this.algLogic.postOneIntermediary(this.intermediaryEntity);
        this.showTable = true;
        this.dialogAdministrationVisible = false;
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }

  getSelectedKantoorNaam(): string {
    const selectedId = this.adminInputFg.get('selectedAdminId')?.value;
    const selectedOption = this.optionsAministratie.find((opt) => opt.value === selectedId);
    return selectedOption?.label || '';
  }

  private buildCustomer(response: CustomerResponse, client: ClientEntity): CustomerRequest {
    const customer = new CustomerRequest({...response});
    // This property (customerGroup) is specifically removed as it's deprecated and will result in a 400 Bad Request if included
    delete customer.customerGroup;

    if (customer.fiscalIdentifier == undefined && client.taxId != undefined) {
      if (client.aanspreekTitel?.includes('heer')) {
        customer.salutation = SalutationType.Sir;
      } else if (client.aanspreekTitel?.includes('vrouw')) {
        customer.salutation = SalutationType.Madam;
      } else if (client.aanspreekTitel?.includes('Erven')) {
        customer.salutation = SalutationType.HeirsOf;
      } else {
        customer.salutation = SalutationType.Other;
      }

      customer.fiscalIdentifier = client.taxId;
      customer.fiscalIdentifierType =
        client.incomeTax || client.typeClient?.includes('natuurlijk')
          ? FiscalIdentifierType.PersonalIdentity
          : FiscalIdentifierType.LegalEntityAndPartnershipIdentification;
    }

    return customer;
  }

  private updateCustomerDuplicates(relationSearchResults: RelationSearchResult[]): Observable<unknown[]> {
    this.clientAddonList = [];
    const ids = relationSearchResults.map((r) => r.id);

    // Lookup by identifier so we can quickly check matches
    const relationByIdentifier = new Map<string, RelationSearchResult>();

    relationSearchResults.forEach((r) => {
      if (r.fiscalIdentifier) {
        relationByIdentifier.set(r.fiscalIdentifier, r);
      }
    });

    return this.bokkzClient.getClientAddons(ids).pipe(
      tap((addons: ClientAddons[]) => {
        this.clientAddonList = addons;
      }),
      switchMap((addons: ClientAddons[]) => {
        const matchingAddons = addons.filter((a) => a.taxId !== undefined && relationByIdentifier.has(a.taxId));
        if (!matchingAddons.length) return of([]);

        const calls = matchingAddons.map((addon) => {
          const relation = addon.taxId ? relationByIdentifier.get(addon.taxId) : null;

          // If you also need to check relation.fiscalIdentifier like earlier, add it here:
          // if (relation.fiscalIdentifier !== undefined) return EMPTY;

          if (relation?.fiscalIdentifier === undefined && addon.taxId !== undefined) {
            return this.customerQueryClient.getCustomer(addon.sstRelationId!).pipe(
              map((response) => this.buildCustomer(response, addon)), // <- adjust if buildCustomer expects different args
              switchMap((customer) => this.customerClient.updateCustomer(customer)),
              catchError((err) => {
                console.error(`Addon ${addon.taxId} failed`, err);
                return EMPTY;
              }),
            );
          }
          return EMPTY;
        });

        return forkJoin(calls);
      }),
    );
  }

  public getDuplicateTaxId(id: string) {
    if (id == undefined || id.length < 2) {
      return undefined;
    }
    const option = this.clientAddonList.find((opt) => opt.taxId === id);
    return option ? option.duplicatetaxIds : undefined;
  }

  public getValidTaxId(id: string) {
    const option = this.clientAddonList.find((opt) => opt.taxId === id);
    if (id == undefined || id.length < 2) {
      return true;
    }
    else if (option?.taxId === undefined) {
      return true;
    }
    else return option ? option.hasValidTaxId : false;
  }

  public getValueUitstel(id: string) {
    const option = this.clientAddonList.find((opt) => opt.sstRelationId === id);
    return option ? option.hasPostponement : undefined;
  }

  public getCouldHaveUitstel(id: string) {
    const option = this.clientAddonList.find((opt) => opt.sstRelationId === id);
    return option ? option.couldHavePostponement : undefined;
  }

  public getValueVennootschapsbelasting(id: string): boolean | undefined {
    const option = this.clientAddonList.find((opt) => opt.sstRelationId === id) as (ClientAddons & {
      corporateTax?: boolean;
    }) | undefined;
    return option?.corporateTax;
  }

  public getValueMuma(id: string) {
    const option = this.clientAddonList.find((opt) => opt.sstRelationId === id);
    return option ? option.activeMumas : undefined;
  }

  public goToMumaDossier(event: Event, id: string, name: string): void {
    event.stopPropagation();
    this.algDataService.updateSstRelationId(id);
    this.bokkzMumaClient
      .getAllMuma(id)
      .pipe()
      .subscribe((value) => {
        if (value) {
          this.clientName = name;
          this.mumaList = value;
          this.dialogMumaVisible = true;
        }
      });
  }

  public changeUitstel(event: Event, id: string, current: boolean): void {
    event.stopPropagation();
    if (!this.getCouldHaveUitstel(id)) {
      this.dialogService.showConfirmDialog({
        title: `Vul eerst de klantgegevens in`,
        body: $localize`Om gebruik te kunnen maken van de uitstelregeling moet je voor je klant een geldig fiscaal nummer invullen en
        een belastingplicht instellen. Het kan zijn dat je na het invullen van deze gegevens de pagina moet verversen om de uitstelregeling te activeren.`,
        confirmText: 'Sluiten',
      });
    }
    else if (current) {
      this.dialogService.showConfirmDeleteDialog({
        title: `Klant afmelden voor uitstelregeling aangiften 2025`,
        body: $localize`Weet je zeker dat je de klant niet wilt opnemen in de uitstelregeling 2025? Let op:
        als je een klant wilt afmelden voor de Uitstelregeling 2024, open dan de "Uitstelregeling" voor je kantoor en meld de klant daar af.`,
        confirmText: 'Deactiveren',
        onConfirm: () =>
          this.bokkzClient.getClient(id).pipe(
            switchMap((client) =>
              this.bokkzClient.postClient({
                ...client,
                postponement: false,
              }),
            ),
          ),
        onConfirmed: (result: ClientEntity) => {
          const entryKey = Object.values(this.clientAddonList).findIndex((v) => v.sstRelationId === id);

          if (!entryKey) return;
          this.clientAddonList[entryKey].hasPostponement = false;
          this.cdr.markForCheck(); // tells Angular to re-render
        },
      });
    } else {
      this.dialogService.showConfirmDialog({
        title: `Klant opnemen in uitstelregeling aangiften 2025`,
        body: $localize`Weet je zeker dat je de de klant wilt aanmelden voor de uitstelregeling aangifte 2025?`,
        confirmText: 'Aanmelden',
        onConfirm: () =>
          this.bokkzClient.getClient(id).pipe(
            switchMap((client) =>
              this.bokkzClient.postClient({
                ...client,
                postponement: true,
              }),
            ),
          ),
        onConfirmed: (result: ClientEntity) => {
          const entryKey = Object.values(this.clientAddonList).findIndex((v) => v.sstRelationId === id);
          if (!entryKey) return;
          this.clientAddonList[entryKey].hasPostponement = result.postponement;
          this.cdr.markForCheck(); // tells Angular to re-render
        },
      });
    }
  }

  public alterInvalidTaxId(event: Event, id: string): void {
    event.stopPropagation();
    this.dialogService.showConfirmDialog({
      title: `Ongeldig fiscaal nummer`,
      body: $localize`Het ingevulde fiscale nummer niet valide. Open de klantkaart door op de drie puntjes
      in deze rij te klikken en pas het fiscale nummer aan`,
      confirmText: 'Afsluiten',
    });
  }

  public alertDuplicateTaxIds(event: Event, id: string): void {
    event.stopPropagation();
    this.duplicateTaxId = id;
    this.duplicateCount = this.getDuplicateTaxId(id);
    this.showDuplicateDialog = true;
  }
}
